/*
 * A Collaterate storefront product image.
 *
 * @property {number} id - (Read only) This product image's unique id.
 * @property {string} attribution - (Read only) The attribution value assigned to this product image.
 * @property {string} alt - (Read only) The alt text value assigned to this product image.
 * @property {string} src - (Read only) The src value for this product image.
 * @property {boolean} isPrimary - (Read only) Whether or not this product image is marked as the primary image.
 * @property {boolean} isThumbnail - (Read only) Whether or not this product image has been marked as a thumbnail image.
 * @property {number[]} variantIds - (Read only) The ids of the product variants this image shows, such as every size
 *     of its red one; empty for an image of the whole product. The same ids the stock product page announces on
 *     `collaterate:productVariantChanged`, so a theme can show the chosen variant's image.
 * @property {?number} variantId - (Read only) The sole id in variantIds when the image shows exactly one variant,
 *     else null. Prefer variantIds.
 * @property {number[]} decorationLocationIds - (Read only) The decoration locations with a print area on this image,
 *     such as its front pocket; empty for an image with none, such as a lifestyle shot.
 */

const integersOf = (list) => Object.freeze(Array.isArray(list) ? list.filter(Number.isInteger) : []);

const variantIdsOf = (data) => {
    if (Array.isArray(data.variantIds)) {
        return integersOf(data.variantIds);
    }
    return Object.freeze(Number.isInteger(data.systemOfferingVariantId) ? [data.systemOfferingVariantId] : []);
};

const soleOf = ([only = null, ...others]) => (others.length ? null : only);

class ProductImage {
    constructor (data) {
        const variantIds = variantIdsOf(data);
        Object.defineProperties(this, {
            id: {
                value: data.id,
                enumerable: true,
            },
            attribution: {
                value: data.attribution || '',
                enumerable: true,
            },
            alt: {
                value: data.imageAlt || '',
                enumerable: true,
            },
            src: {
                value: data.imageUrl,
                enumerable: true,
            },
            isPrimary: {
                value: Boolean(data.primary),
                enumerable: true,
            },
            isThumbnail: {
                value: Boolean(data.thumbnail),
                enumerable: true,
            },
            variantId: {
                value: soleOf(variantIds),
                enumerable: true,
            },
            variantIds: {
                value: variantIds,
                enumerable: true,
            },
            decorationLocationIds: {
                value: integersOf(data.decorationLocationIds),
                enumerable: true,
            },
        });
    }
}

export default ProductImage;
